<script setup>
import { useStore } from 'vuex';
import { PopupModal } from '@/components/auth';
import { useDialogManager } from '@/composables/useDialogManager';
import { useRnBridge } from '@/composables/useRnBridge';

import { useProductThumbnail } from '@/composables/useProductThumbnail';
import { validateGiftCode } from '@/api/gifts';
import { DEV_MODE } from '@/config/dev-mode';
import VerifyPhone from '@/components/purchase/VerifyPhone.vue';
import GiftValid from '@/components/purchase/GiftValid.vue';
import GiftResult from '@/components/purchase/GiftResult.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';

const { ensureProductMaps, getDetailImageUrls, getDetailType } = useProductThumbnail();

definePage({ meta: { layout: 'purchase' } });

const route = useRoute();
const router = useRouter();
const store = useStore();
const code = computed(() => route.query.code || '');

// 로그인 체크
const token = computed(() => store.getters['user/token']);

const {
  dialogTitle,
  dialogTitle2,
  dialogText,
  dialogNextUrl,
  dialogWarning,
  dialogCancel,
  confirmRef,
  showConfirm,
} = useDialogManager();

// 상태 관리
const isLoginChecked = ref(false);
const isVerified = ref(false);
const isAccepted = ref(false);
const isCancelled = ref(false);
const giftInfo = ref(null);

// 상품별 상세 이미지 (배열)
const detailImageUrls = computed(() => {
  const productId = giftInfo.value?.productId;
  const productName = giftInfo.value?.productName;
  if (!productId) return [];
  return getDetailImageUrls(productId, productName);
});

// 상품별 detailType
const detailType = computed(() => {
  const productId = giftInfo.value?.productId;
  const productName = giftInfo.value?.productName;
  if (!productId) return null;
  return getDetailType(productId, productName);
});

// 팝업
const showPopup = ref(false);
const popupMessage = ref('');
const pendingRedirect = ref(null);

// 로그인 확인
const confirmLogin = () => {
  router.push({ path: '/auth/login', query: { to: route.fullPath } });
};

// 로그인 체크
const checkLogin = () => {
  if (!sessionStorage.getItem('X-Token')) {
    showConfirm({
      title: '로그인 후 이용 가능합니다.',
      title2: '로그인 페이지로 이동하시겠습니까?',
      text: '확인',
      onConfirm: () => confirmLogin(),
      onCancel: () => router.replace('/purchase/tickets'),
    });
    return;
  }
  isLoginChecked.value = true;
};

onMounted(() => {
  checkLogin();
});

// 로그인 후 토큰이 들어오면 게이트 해제
watch(token, (newToken) => {
  if (newToken && !isLoginChecked.value) {
    isLoginChecked.value = true;
  }
});

// VerifyPhone 이벤트 핸들러
const onVerified = async (info) => {
  await ensureProductMaps();
  giftInfo.value = info;
  isVerified.value = true;
};

const onVerifyError = (errorMessage) => {
  popupMessage.value = errorMessage;
  pendingRedirect.value = '/purchase/gift-box';
  showPopup.value = true;
};

const onExpired = () => {
  popupMessage.value = '만료된 선물입니다.';
  pendingRedirect.value = '/purchase/gift-box';
  showPopup.value = true;
};

const onAccepted = () => {
  popupMessage.value = '이미 수락된 선물입니다.';
  pendingRedirect.value = '/purchase/gift-box';
  showPopup.value = true;
};

const onVerifyCancel = () => {
  router.replace('/purchase/gift-box');
};

const onNavigate = (path) => {
  router.push(path);
};

// GiftValid 이벤트 핸들러
const onGiftCancel = () => {
  isCancelled.value = true;
};

const onGiftSuccess = () => {
  isAccepted.value = true;
};

const onGiftError = (errorMessage) => {
  popupMessage.value = errorMessage;
  showPopup.value = true;
};

// GiftResult 이벤트 핸들러
const onGiftAction = () => {
  router.replace('/purchase/gift-box');
};

const onCancelAction = () => {
  router.replace('/purchase/gift-box');
};

const closePopup = () => {
  showPopup.value = false;
  if (pendingRedirect.value) {
    router.replace(pendingRedirect.value);
    pendingRedirect.value = null;
  }
};

// 개발용: 인증 단계 건너뛰기
const skipVerification = async () => {
  if (!code.value) {
    alert('code 파라미터가 없습니다.');
    return;
  }
  try {
    const info = await validateGiftCode(code.value);
    await onVerified(info);
  } catch (e) {
    console.error('건너뛰기 실패:', e);
    alert('선물 정보 조회 실패');
  }
};
</script>

<template>
  <div class="gift-claim" :class="{ 'gift-claim--verified': isVerified }">
    <!-- 1. 로그인 체크 완료 후 -->
    <template v-if="isLoginChecked">
      <!-- 2. 휴대폰 인증 -->
      <VerifyPhone
        v-if="!isVerified"
        :code="code"
        @verified="onVerified"
        @error="onVerifyError"
        @expired="onExpired"
        @accepted="onAccepted"
        @cancel="onVerifyCancel"
        @navigate="onNavigate"
      />
      <!-- 개발용 건너뛰기 버튼 -->
      <button v-if="DEV_MODE && !isVerified" class="gift-claim__dev-skip" @click="skipVerification">
        건너뛰기(개발)
      </button>

      <!-- 3. 선물 수락 -->
      <div
        v-else-if="!isAccepted && !isCancelled"
        class="gift-claim__container gift-claim__container--verified"
      >
        <GiftValid
          :gift-info="giftInfo"
          :code="code"
          :has-detail="detailImageUrls.length > 0"
          :detail-type="detailType"
          @cancel="onGiftCancel"
          @success="onGiftSuccess"
          @error="onGiftError"
        >
          <template #detail>
            <div v-if="detailImageUrls.length > 0" class="gift-claim__detail-image">
              <img
                v-for="(url, index) in detailImageUrls"
                :key="index"
                :src="url"
                alt="상품 상세"
              />
            </div>
          </template>
        </GiftValid>
      </div>

      <!-- 4. 결과 -->
      <div v-else class="gift-claim__container gift-claim__container--verified">
        <GiftResult v-if="isAccepted" type="accepted" @action="onGiftAction" />
        <GiftResult v-else type="cancelled" @action="onCancelAction" />
      </div>
    </template>

    <PopupModal
      v-if="showPopup"
      :message="popupMessage"
      button-text="확인"
      :is-visible="showPopup"
      @close="closePopup"
      @confirm="closePopup"
    />

    <ConfirmComponent
      ref="confirmRef"
      :title="dialogTitle"
      :title2="dialogTitle2"
      :text="dialogText"
      :nextUrl="dialogNextUrl"
      :warning="dialogWarning"
      :onCancel="dialogCancel"
    />
  </div>
</template>

<style lang="scss" scoped>
.gift-claim {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-family: Pretendard, sans-serif;

  &--verified {
    padding: 0;
  }

  &__container {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 400px;
    width: 100%;

    &--verified {
      max-width: 420px;
      padding: 0;
      background: transparent;
      border-radius: 0;
      box-shadow: none;
    }
  }

  &__detail-image {
    width: 100%;

    img {
      width: 100%;
      height: auto;
      display: block;
    }
  }

  &__dev-skip {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    padding: 8px 16px;
    background: #ff6b6b;
    color: #fff;
    border: none;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    z-index: 9999;
    opacity: 0.8;

    &:hover {
      opacity: 1;
    }
  }
}

@media (max-width: 768px) {
  .gift-claim {
    background: #fff;

    &__container {
      max-width: 100%;
      padding: 0;
      border-radius: 0;
      box-shadow: none;
    }
  }
}
</style>
